import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["jsCheck", "submitBtn"];

  connect() {
    this.enableJavaScriptCheck();
    this.initializeSubmitButton();
    this.addFormValidation();
    this.addTurboEventListeners();
  }

  enableJavaScriptCheck() {
    // Set JavaScript check value to indicate JS is enabled
    if (this.hasJsCheckTarget) {
      this.jsCheckTarget.value = "js_enabled";
    }
  }

  initializeSubmitButton() {
    // Disable submit button initially and enable after delay
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true;

      // Add loading text
      const originalText = this.submitBtnTarget.textContent;
      this.submitBtnTarget.textContent = "Loading...";

      // Enable submit button after 1 second to prevent instant bot submissions
      setTimeout(() => {
        this.submitBtnTarget.disabled = false;
        this.submitBtnTarget.textContent = originalText;
      }, 1000);
    }

    // Add real-time validation
    this.addRealTimeValidation();
  }

  addFormValidation() {
    // Add submit event listener for additional validation
    this.element.addEventListener("submit", this.validateSubmission.bind(this));
  }

  addRealTimeValidation() {
    // Add validation for required fields
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "company",
      "subject",
      "message",
    ];

    requiredFields.forEach((fieldName) => {
      const field = this.element.querySelector(
        `[name="contact_form[${fieldName}]"]`,
      );
      if (field) {
        field.addEventListener("blur", () => this.validateField(field));
        field.addEventListener("input", () => this.clearFieldError(field));
      }
    });
  }

  validateField(field) {
    const fieldName = field.name.match(/\[(.*?)\]/)[1];
    const value = field.value.trim();

    // Remove existing error
    this.clearFieldError(field);

    if (!value) {
      this.showFieldError(
        field,
        `${this.formatFieldName(fieldName)} is required`,
      );
      return false;
    }

    // Email validation
    if (fieldName === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        this.showFieldError(field, "Please enter a valid email address");
        return false;
      }
    }

    return true;
  }

  clearFieldError(field) {
    const errorElement = field.parentElement.querySelector(".field-error");
    if (errorElement) {
      errorElement.remove();
    }
    field.classList.remove("border-destructive");
    field.classList.add("border-input");
  }

  showFieldError(field, message) {
    // Add error styling
    field.classList.remove("border-input");
    field.classList.add("border-destructive");

    // Create error message
    const errorElement = document.createElement("p");
    errorElement.className = "text-sm text-destructive field-error";
    errorElement.textContent = message;

    field.parentElement.appendChild(errorElement);
  }

  formatFieldName(fieldName) {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  validateSubmission(event) {
    // First validate all required fields
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "company",
      "subject",
      "message",
    ];

    let hasErrors = false;

    requiredFields.forEach((fieldName) => {
      const field = this.element.querySelector(
        `[name="contact_form[${fieldName}]"]`,
      );
      if (field && !this.validateField(field)) {
        hasErrors = true;
      }
    });

    if (hasErrors) {
      event.preventDefault();
      this.showError("Please fill in all required fields correctly.");
      return false;
    }

    // Simple math challenge - invisible to users but required by JS
    const mathResult = 7 + 3;
    if (mathResult !== 10) {
      event.preventDefault();
      return false;
    }

    // Additional timing check - ensure user spent reasonable time on form
    const formStartTime = this.element.querySelector(
      'input[name="contact_form[form_start_time]"]',
    );
    if (formStartTime && formStartTime.value) {
      try {
        const startTime = new Date(formStartTime.value);
        const currentTime = new Date();
        const timeSpent = (currentTime - startTime) / 1000; // in seconds

        // Block if filled too quickly (less than 2 seconds)
        if (timeSpent < 2) {
          event.preventDefault();
          this.showError(
            "Please take a moment to review your message before submitting.",
          );
          return false;
        }
      } catch (error) {
        // If there's an error parsing the time, block submission
        event.preventDefault();
        return false;
      }
    }

    // Show loading state
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true;
      this.submitBtnTarget.textContent = "Sending...";
    }

    return true;
  }

  showError(message) {
    // Create or update error message
    let errorDiv = this.element.querySelector(".js-form-error");

    if (!errorDiv) {
      errorDiv = document.createElement("div");
      errorDiv.className =
        "js-form-error rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 mb-4";
      errorDiv.innerHTML = `
        <div class="flex">
          <div class="ml-3">
            <p class="text-sm font-medium text-red-800 dark:text-red-200">${message}</p>
          </div>
        </div>
      `;
      this.element.insertBefore(errorDiv, this.element.firstChild);
    } else {
      errorDiv.querySelector("p").textContent = message;
    }

    // Auto-remove error after 5 seconds
    setTimeout(() => {
      if (errorDiv && errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 5000);
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    this.element.removeEventListener(
      "submit",
      this.validateSubmission.bind(this),
    );

    // Clean up Turbo event listeners
    document.removeEventListener("turbo:frame-load", this.handleTurboFrameLoad);
  }

  // Handle successful form submission
  handleSuccess() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false;
      this.submitBtnTarget.textContent = "Send Message";
    }

    // Clear all form fields
    const form = this.element.querySelector("form");
    if (form) {
      form.reset();

      // Clear any field errors
      const errorElements = this.element.querySelectorAll(".field-error");
      errorElements.forEach((error) => error.remove());

      // Reset field styling
      const fields = this.element.querySelectorAll("input, select, textarea");
      fields.forEach((field) => {
        field.classList.remove("border-destructive");
        field.classList.add("border-input");
      });
    }

    // Re-initialize the form
    this.enableJavaScriptCheck();
  }

  addTurboEventListeners() {
    // Listen for turbo frame loads to re-initialize after form replacement
    this.handleTurboFrameLoad = this.handleTurboFrameLoad.bind(this);
    document.addEventListener("turbo:frame-load", this.handleTurboFrameLoad);
  }

  handleTurboFrameLoad(event) {
    // Check if the loaded frame is our contact form
    if (event.target.id === "contact_form") {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (this.element) {
          this.enableJavaScriptCheck();
          this.initializeSubmitButton();
        }
      }, 100);
    }
  }
}
